
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tractor } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import Layout from '@/components/layout/Layout';

import FarmPricesHeader from '@/components/farm-prices/FarmPricesHeader';
import FarmPricesFilter from '@/components/farm-prices/FarmPricesFilter';
import FarmPricesTable from '@/components/farm-prices/FarmPricesTable';
import FarmPriceForm from '@/components/farm-prices/FarmPriceForm';
import FarmPriceUpload from '@/components/farm-prices/FarmPriceUpload';

import { useFarmPrices, useCreateFarmPrice, useUpdateFarmPrice, useDeleteFarmPrice, useBulkCreateFarmPrices } from '@/hooks/use-farm-prices';
import { FarmPrice, FarmPriceFilters, CreateFarmPriceInput } from '@/types/database/farm-prices';

const FarmPrices: React.FC = () => {
  const [filters, setFilters] = useState<FarmPriceFilters>({});
  const [showForm, setShowForm] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editingPrice, setEditingPrice] = useState<FarmPrice | undefined>();
  const [deleteId, setDeleteId] = useState<string>('');

  const { data: farmPrices = [], isLoading } = useFarmPrices(filters);
  const createMutation = useCreateFarmPrice();
  const updateMutation = useUpdateFarmPrice();
  const deleteMutation = useDeleteFarmPrice();
  const bulkCreateMutation = useBulkCreateFarmPrices();

  const handleAddNew = () => {
    setEditingPrice(undefined);
    setShowForm(true);
  };

  const handleEdit = (price: FarmPrice) => {
    setEditingPrice(price);
    setShowForm(true);
  };

  const handleFormSubmit = (data: CreateFarmPriceInput) => {
    if (editingPrice) {
      updateMutation.mutate({ id: editingPrice.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
  };

  const confirmDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
      setDeleteId('');
    }
  };

  const handleUpload = (prices: CreateFarmPriceInput[]) => {
    bulkCreateMutation.mutate(prices);
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto p-6">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Tractor className="h-8 w-8 text-green-600" />
              <h1 className="text-3xl font-bold text-gray-900">Farm Prices</h1>
            </div>
            <p className="text-gray-600">
              Track and manage prices directly from farms
            </p>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Loading...</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto p-6">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Tractor className="h-8 w-8 text-green-600" />
            <h1 className="text-3xl font-bold text-gray-900">Farm Prices</h1>
          </div>
          <p className="text-gray-600">
            Track and manage prices directly from farms
          </p>
        </div>

        <FarmPricesHeader
          onAddNew={handleAddNew}
          onUpload={() => setShowUpload(true)}
        />

        <FarmPricesFilter
          filters={filters}
          onFiltersChange={setFilters}
          onClearFilters={handleClearFilters}
        />

        <FarmPricesTable
          prices={farmPrices}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        <FarmPriceForm
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          editingPrice={editingPrice}
        />

        <FarmPriceUpload
          open={showUpload}
          onClose={() => setShowUpload(false)}
          onUpload={handleUpload}
        />

        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId('')}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Farm Price</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this farm price record? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default FarmPrices;
